import { useEffect } from 'react'
import { Link } from 'react-router-dom'
import { ensureCmsCards, useCmsVersion } from '../cms/store'
import AuthorSocials from '../components/AuthorSocials'
import MediaImage from '../components/MediaImage'
import { revealDelay } from '../lib/reveal'
import { allAuthors, pages, storiesByAuthor } from '../data/content'

const SKIP = new Set(['PJ Haarsma', 'Brian Rimer', 'admin'])

function ContributorCard({ person, stagger = 0 }: { person: ReturnType<typeof allAuthors>[number]; stagger?: number }) {
  const posts = storiesByAuthor(person.name)
  const authorHref = `/author/${person.slug}/`

  const photo = person.photo ? (
    <MediaImage
      src={person.photo}
      size="card"
      alt={person.name}
      className="aspect-[3/4] w-full object-cover object-top"
    />
  ) : (
    <div className="grid aspect-[3/4] w-full place-items-center bg-sand text-caps-display text-ink">
      {person.name.slice(0, 1)}
    </div>
  )

  return (
    <article data-reveal style={revealDelay((stagger % 4) * 0.08)} className="flex flex-col">
      {posts.length > 0 ? (
        <Link to={authorHref} className="overflow-hidden bg-sand">
          {photo}
        </Link>
      ) : (
        <div className="overflow-hidden bg-sand">{photo}</div>
      )}
      <h2 className="text-caps-h3 mt-2.5 m-0 text-ink">{person.name}</h2>
      {person.socials?.length ? <AuthorSocials socials={person.socials} /> : null}
      {person.bio ? (
        <p className="mt-1.5 m-0 text-[14px] leading-snug text-copy">{person.bio}</p>
      ) : null}
      {posts.length > 0 ? (
        <Link
          to={authorHref}
          className="text-caps mt-2 inline-block text-[11px] text-muted hover:text-rose"
        >
          Read posts
        </Link>
      ) : null}
    </article>
  )
}

export default function Contributors() {
  useCmsVersion()

  useEffect(() => {
    void ensureCmsCards()
  }, [])
  const { kicker, title, dek } = pages.contributors
  const people = allAuthors()
    .filter((author) => !SKIP.has(author.name))
    .sort((a, b) => {
      if (a.slug === 'sky') return -1
      if (b.slug === 'sky') return 1
      return a.name.localeCompare(b.name)
    })

  return (
    <main className="py-[clamp(20px,3vw,36px)]">
      <header data-reveal>
        <p className="text-caps m-0 text-rose">{kicker}</p>
        <h1 className="text-caps-hero mt-2 m-0 text-balance text-ink">{title}</h1>
        {dek ? <p className="mt-3 m-0 max-w-[46ch] text-[15px] leading-snug text-copy">{dek}</p> : null}
      </header>

      <div className="mt-6 grid grid-cols-1 gap-x-5 gap-y-7 min-[480px]:grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 lg:gap-x-6 lg:gap-y-8">
        {people.map((person, index) => (
          <ContributorCard key={person.slug} person={person} stagger={index} />
        ))}
      </div>

      <Link
        to="/contact/"
        data-reveal
        className="text-caps mt-8 inline-flex items-center gap-2 border-b border-ink pb-[3px] text-[11px] hover:border-rose hover:text-rose"
      >
        Write for TIGG
      </Link>
    </main>
  )
}
